export type ModuleSpecifierMatch = 'exact' | 'prefix';

export interface ModuleSpecifierConfig {
  match: ModuleSpecifierMatch;
  value: string;
  behavior?:
    | 'default'
    | 'react-jsx-runtime'
    | 'react-jsx-dev-runtime'
    | 'namespace';
}

export interface EmbeddedLibraryDefinition {
  libraryKey: string;
  assetKey: string;
  packageName: string;
  version: string;
  globalVar: string;
  cdnPath: string;
  moduleSpecifiers: ModuleSpecifierConfig[];
  loadStage: number; // Load order: 0 first, then 1, etc. Enables parallel loading within stages
  enabled: boolean; // Whether this library is ready for use
  sha384: string; // Subresource Integrity hash, format 'sha384-<base64>' (R32)
}

export const DEFAULT_SHARED_LIB_CDN_BASE =
  'https://venus-static-01293ak.web.app/libs';

export const EMBEDDED_LIBRARIES: EmbeddedLibraryDefinition[] = [
  {
    libraryKey: 'phaser@3.90.0',
    assetKey: 'library:phaser@3.90.0',
    packageName: 'phaser',
    version: '3.90.0',
    globalVar: 'Phaser',
    cdnPath: 'phaser/3.90.0/phaser.min.js',
    moduleSpecifiers: [{ match: 'exact', value: 'phaser' }],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-Fi7F0CYQBHYa+s2vvYJeiqvOQf3tuZ28vafRGXvMt7ijxNTVf3+eXPkNRIUi9JD2',
  },
  {
    libraryKey: 'react@18.3.1',
    assetKey: 'library:react@18.3.1',
    packageName: 'react',
    version: '18.3.1',
    globalVar: 'React',
    cdnPath: 'react/18.3.1/react.production.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'react', behavior: 'namespace' },
      { match: 'exact', value: 'react/jsx-runtime', behavior: 'react-jsx-runtime' },
      {
        match: 'exact',
        value: 'react/jsx-dev-runtime',
        behavior: 'react-jsx-dev-runtime',
      },
    ],
    loadStage: 0, // Must load before ReactDOM
    enabled: true,
    sha384: 'sha384-DGyLxAyjq0f9SPpVevD6IgztCFlnMF6oW/XQGmfe+IsZ8TqEiDrcHkMLKI6fiB/Z',
  },
  {
    libraryKey: 'react-dom@18.3.1',
    assetKey: 'library:react-dom@18.3.1',
    packageName: 'react-dom',
    version: '18.3.1',
    globalVar: 'ReactDOM',
    cdnPath: 'react-dom/18.3.1/react-dom.production.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'react-dom', behavior: 'namespace' },
      { match: 'exact', value: 'react-dom/client', behavior: 'namespace' },
    ],
    loadStage: 1, // Depends on React (stage 0)
    enabled: true,
    sha384: 'sha384-gTGxhz21lVGYNMcdJOyq01Edg0jhn/c22nsx0kyqP0TxaV5WVdsSH1fSDUf5YJj1',
  },
  {
    libraryKey: 'react@19.2.4',
    assetKey: 'library:react@19.2.4',
    packageName: 'react',
    version: '19.2.4',
    globalVar: 'React',
    cdnPath: 'react/19.2.4/react.production.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'react', behavior: 'namespace' },
      { match: 'exact', value: 'react/jsx-runtime', behavior: 'react-jsx-runtime' },
      {
        match: 'exact',
        value: 'react/jsx-dev-runtime',
        behavior: 'react-jsx-dev-runtime',
      },
    ],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-8TkCbSOgofgjSE0OOifjZ5+r2NAV5g6BKvoEZwDU9O/xKL/TXRC09alkbdtKMor1',
  },
  {
    libraryKey: 'react-dom@19.2.4',
    assetKey: 'library:react-dom@19.2.4',
    packageName: 'react-dom',
    version: '19.2.4',
    globalVar: 'ReactDOM',
    cdnPath: 'react-dom/19.2.4/react-dom.production.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'react-dom', behavior: 'namespace' },
      { match: 'exact', value: 'react-dom/client', behavior: 'namespace' },
    ],
    loadStage: 1,
    enabled: true,
    sha384: 'sha384-K8Ud9uS+B5cudnJG4B8QvXCOch6TVxmFwyo5I30ZkLAuXtuNGyP6XFoY4kRBt67C',
  },
  {
    libraryKey: 'three@0.170.0',
    assetKey: 'library:three@0.170.0',
    packageName: 'three',
    version: '0.170.0',
    globalVar: 'THREE',
    cdnPath: 'three/r170/three.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'three', behavior: 'namespace' },
    ],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-x6G0n+1pQmOpeke5EcTTpGX9XhLRpukt9fqgE/56XeFyk/KQGJoEWIAWvTA5z6Hx',
  },
  {
    libraryKey: 'three@0.183.2',
    assetKey: 'library:three@0.183.2',
    packageName: 'three',
    version: '0.183.2',
    globalVar: 'THREE',
    cdnPath: 'three/r183/three.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'three', behavior: 'namespace' },
    ],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-qeAovMiAAbBeXlBZKbnt7ZyMbv57Uzj+9MJEjLlb/VrZDhfss9Pwe27P2G24CWgz',
  },
  {
    libraryKey: 'matter-js@0.19.0',
    assetKey: 'library:matter-js@0.19.0',
    packageName: 'matter-js',
    version: '0.19.0',
    globalVar: 'Matter',
    cdnPath: 'matter-js/0.19.0/matter.min.js',
    moduleSpecifiers: [{ match: 'exact', value: 'matter-js' }],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-OqQP3UcU7efkEYDRjGmQou2uEvzGFGRtwdYXTjnupeB9cWogSgQ4BOhyklFBYbBR',
  },
  {
    libraryKey: 'inkjs@2.2.0',
    assetKey: 'library:inkjs@2.2.0',
    packageName: 'inkjs',
    version: '2.2.0',
    globalVar: 'inkjs',
    cdnPath: 'inkjs/2.2.0/ink-full.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'inkjs', behavior: 'namespace' },
      { match: 'exact', value: 'inkjs/full', behavior: 'namespace' },
    ],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-vCtiM63EhiADRQPG1Z+ZTu47GpM69knpj/YZTHbnpNU539BD0yCSUx+3x5k1ZJlU',
  },
  {
    libraryKey: 'inkjs@2.3.2',
    assetKey: 'library:inkjs@2.3.2',
    packageName: 'inkjs',
    version: '2.3.2',
    globalVar: 'inkjs',
    cdnPath: 'inkjs/2.3.2/ink-full.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'inkjs', behavior: 'namespace' },
      { match: 'exact', value: 'inkjs/full', behavior: 'namespace' },
    ],
    loadStage: 0,
    enabled: true,
    sha384: 'sha384-pMC1d/5lwvaWRu/KWeqZMZPLhvf69CDNNCW5viyAW2+m78bVF+OUTPlaILfuH3DE',
  },
  {
    libraryKey: 'zustand@5.0.3',
    assetKey: 'library:zustand@5.0.3',
    packageName: 'zustand',
    version: '5.0.3',
    globalVar: 'zustand',
    cdnPath: 'zustand/5.0.3/zustand.min.js',
    moduleSpecifiers: [
      { match: 'exact', value: 'zustand', behavior: 'namespace' },
      { match: 'exact', value: 'zustand/middleware', behavior: 'namespace' },
    ],
    loadStage: 1, // Depends on React (stage 0)
    enabled: true,
    sha384: 'sha384-dOaRa/FSD3ckw1ovPQ2TDv9aBiRCTiCTO12xgjTt0eHmpDFamMyxZb2+pDp0o62a',
  },
];

export const EMBEDDED_LIBRARY_BY_KEY: Record<string, EmbeddedLibraryDefinition> =
  EMBEDDED_LIBRARIES.reduce(
    (acc, lib) => {
      acc[lib.libraryKey] = lib;
      return acc;
    },
    {} as Record<string, EmbeddedLibraryDefinition>,
  );

export const MODULE_TO_LIBRARY_SPECIFIERS = EMBEDDED_LIBRARIES.filter(
  (lib) => lib.enabled,
).flatMap((lib) =>
  lib.moduleSpecifiers.map((specifier) => ({
    ...specifier,
    libraryKey: lib.libraryKey,
  })),
);

/**
 * Get library definition by key, throwing if not found.
 * @param libraryKey - Library key (e.g., 'phaser@3.90.0')
 * @returns Library definition
 * @throws Error if library key is not found
 */
export function getLibraryDefinition(
  libraryKey: string,
): EmbeddedLibraryDefinition {
  const definition = EMBEDDED_LIBRARY_BY_KEY[libraryKey]
  if (!definition) {
    const availableKeys = Object.keys(EMBEDDED_LIBRARY_BY_KEY).join(', ')
    throw new Error(
      `Unsupported embedded library: ${libraryKey}. Available libraries: ${availableKeys}`,
    )
  }
  return definition
}
